import React, { useEffect, useState, useCallback } from 'react';
import InstructionCard from '../components/InstructionCard';
import LivesDisplay from '../components/LivesDisplay';
import styles from './TransactionsPage.module.css';
import BottomNavBar from '../components/BottomNavBar';
import CategoryMatcherGame from './CategoryMatcherGame';
import plaidService, { Transaction } from '../services/plaidService';

const TransactionsPage: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  
  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await plaidService.getPersistedTransactions();
      setTransactions(data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Listen for transaction updates from CategoryMatcherGame
  useEffect(() => {
    const handleTransactionsUpdated = async () => {
      try {
        // Clear category matcher results from localStorage since they're now in the database
        localStorage.removeItem('category_matcher_results');
        // Refresh transactions to show updated categories
        await fetchTransactions();
      } catch (err) {
        console.error('Error refreshing transactions after update:', err);
      }
    };

    window.addEventListener('transactionsUpdated', handleTransactionsUpdated);
    
    return () => {
      window.removeEventListener('transactionsUpdated', handleTransactionsUpdated);
    };
  }, [fetchTransactions]);

  return (
    <div className={styles.container} style={{
      paddingBottom: 200,
      overflowY: 'auto',
      height: '100vh',
      WebkitOverflowScrolling: 'touch'
    }}>
      <h1 style={{ 
        color: 'hsl(260, 80%, 74%)', 
        fontSize: '2.5rem', 
        fontWeight: 700, 
        marginTop: '75px', 
        marginBottom: '1rem' 
      }}>Category Matcher</h1>
      <LivesDisplay />
      <InstructionCard
        title="Categorize Your Spending"
        content={[
          "Connect your bank account to see all your transactions. Use the Category Matcher game to categorize your expenses and understand where your money goes.",
          "Set your income to unlock your first achievement and get personalized budget recommendations.",
          "The more you categorize, the more you learn and the more XP you earn!"
        ]}
        emoji="📊"
        storageKey="transactions_instructions_dismissed"
      />

      {/* Category Matcher Game Section */}
      <div style={{ margin: '32px 0 32px 0' }}>
        <CategoryMatcherGame />
      </div>

      {/* Transaction List Section */}
      <div style={{ margin: '32px 0' }}>
        <h2 style={{ 
          color: 'hsl(260, 80%, 74%)', 
          fontSize: '1.8rem', 
          fontWeight: 700, 
          marginBottom: '1rem' 
        }}>Your Transactions</h2>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            Loading transactions...
          </div>
        ) : transactions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <p>No transactions found. Connect your bank account to get started!</p>
          </div>
        ) : (
          <div>
            {transactions.map((transaction) => (
              <div key={transaction.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                background: '#fff',
                marginBottom: '8px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '4px' }}>
                    {transaction.name}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>
                    {Array.isArray(transaction.category) 
                      ? transaction.category.join(' > ') 
                      : transaction.category || 'Uncategorized'}
                  </div>
                  <div style={{ fontSize: '0.9rem', marginBottom: '4px' }}>
                    <strong>Budget Category:</strong> {' '}
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      backgroundColor: transaction.budget_category === 'Need' ? '#fef2f2' : 
                                     transaction.budget_category === 'Want' ? '#fff7ed' :
                                     transaction.budget_category === 'Savings' ? '#f0fdf4' :
                                     transaction.budget_category === 'Income' ? '#f0f9ff' : '#f9fafb',
                      color: transaction.budget_category === 'Need' ? '#dc2626' : 
                             transaction.budget_category === 'Want' ? '#ea580c' :
                             transaction.budget_category === 'Savings' ? '#16a34a' :
                             transaction.budget_category === 'Income' ? '#2563eb' : '#6b7280'
                    }}>
                      {transaction.budget_category || 'Uncategorized'}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                    {new Date(transaction.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                </div>
                <div style={{ 
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  color: transaction.amount < 0 ? '#16a34a' : '#dc2626',
                  textAlign: 'right'
                }}>
                  {transaction.amount < 0 ? '+' : '-'}${Math.abs(transaction.amount).toFixed(2)}
                  {transaction.pending && (
                    <div style={{ fontSize: '0.7rem', color: '#f59e0b', marginTop: '2px' }}>
                      Pending
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNavBar />
    </div>
  );
};

export default TransactionsPage; 